import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  FaNetworkWired, FaHistory, FaHandshake,
  FaChartLine, FaArrowUp, FaArrowDown, FaBalanceScale,
  FaUserFriends, FaHandsHelping
} from 'react-icons/fa';
import { MdShowChart } from 'react-icons/md';
import './SocialCredit.css';

const SocialCreditGraph = () => {
  const [score, setScore]       = useState(522);
  const [networkSize]           = useState(42);
  const [transactions]          = useState(156);
  const [reputation]            = useState(95);
  const hoveredNodeState        = useState(null);
  const creditRef               = useRef(null);

  const nodes = {
    you:       { x: 300, y: 200, score: 522, name: 'You',       color: '#00D4AA' },
    rahul:     { x: 180, y: 100, score: 400, name: 'Rahul',     color: '#6C63FF' },
    vedika:    { x: 420, y: 100, score: 500, name: 'Vedika',    color: '#FF4B6E' },
    satan:     { x: 300, y: 300, score: 400, name: 'Satan',     color: '#F5C842' },
    subham:    { x: 80,  y: 50,  score: 350, name: 'Subham',    color: '#9D4EDD' },
    sonam:     { x: 520, y: 50,  score: 450, name: 'Sonam',     color: '#36D1DC' },
    community: { x: 150, y: 350, score: null, name: 'Community', color: '#2ECC71', type: 'pod' },
    college:   { x: 450, y: 350, score: null, name: 'College',   color: '#3498DB', type: 'pod' },
    ngo:       { x: 300, y: 450, score: null, name: 'NGO',       color: '#F39C12', type: 'pod' }
  };

  const creditFactors = [
    { id: 1, name: "Repayment History",   value: 95, weight: 35, icon: <FaHistory />,     description: "Timeliness of repayments to friends",        trend: "up" },
    { id: 2, name: "Network Strength",    value: 85, weight: 25, icon: <FaNetworkWired />, description: "Quality and size of your network",           trend: "up" },
    { id: 3, name: "Transaction Volume",  value: 75, weight: 20, icon: <FaChartLine />,    description: "Number of successful transactions",          trend: "up" },
    { id: 4, name: "Peer Endorsements",   value: 90, weight: 15, icon: <FaHandshake />,    description: "Recommendations from connections",           trend: "up" },
    { id: 5, name: "Credit Utilization",  value: 60, weight: 5,  icon: <FaBalanceScale />, description: "Percentage of available credit used",        trend: "down" }
  ];

  useEffect(() => {
    const id = setInterval(() => {
      setScore(prev => Math.max(0, Math.min(600, prev + (Math.random() > 0.5 ? 1 : -1))));
    }, 5000);
    return () => clearInterval(id);
  }, []);

  const getScoreColor = (s) => {
    if (s >= 500) return '#00D4AA';
    if (s >= 400) return '#F5C842';
    if (s >= 300) return '#FFB347';
    return '#FF4B6E';
  };

  const getScoreLabel = (s) => {
    if (s >= 500) return 'Excellent';
    if (s >= 400) return 'Good';
    if (s >= 300) return 'Fair';
    return 'Needs Improvement';
  };

  const connections = [
    { from: 'you', to: 'rahul' }, { from: 'you', to: 'vedika' },
    { from: 'you', to: 'satan' }, { from: 'rahul', to: 'subham' },
    { from: 'vedika', to: 'sonam' }, { from: 'you', to: 'community' },
    { from: 'you', to: 'college' }, { from: 'you', to: 'ngo' }
  ];

  return (
    <section className="social-credit-section" id="social-credit" ref={creditRef}>
      <div className="container">

        {/* Section header */}
        <div className="section-header">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Social Credit Graph
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            Your financial reputation based on real social transactions
          </motion.p>
        </div>

        <div className="credit-dashboard">

          {/* ── Score Overview ── */}
          <motion.div
            className="score-overview"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="score-display">
              <div
                className="score-circle"
                style={{
                  background: `conic-gradient(${getScoreColor(score)} ${(score / 600) * 360}deg, #1A1A26 ${(score / 600) * 360}deg 360deg)`
                }}
              >
                <div className="score-inner">
                  <h3>{score}<span className="score-max"> / 600</span></h3>
                  <span className="score-label">Rupayya Score</span>
                  <span className="score-rating" data-rating={getScoreLabel(score)}>
                    {getScoreLabel(score)}
                  </span>
                </div>
              </div>

              <div className="score-details">
                <div className="detail">
                  <FaUserFriends />
                  <div><h4>{networkSize}</h4><p>Network Size</p></div>
                </div>
                <div className="detail">
                  <MdShowChart />
                  <div><h4>{transactions}</h4><p>Transactions</p></div>
                </div>
                <div className="detail">
                  <FaHandsHelping />
                  <div><h4>{reputation}%</h4><p>Reputation</p></div>
                </div>
              </div>
            </div>

            <div className="score-breakdown">
              <h4>How Your Score is Calculated</h4>
              {creditFactors.map((factor, index) => (
                <div key={factor.id} className="breakdown-item">
                  <div className="breakdown-header">
                    <div className="factor-icon">{factor.icon}</div>
                    <div>
                      <h5>{factor.name}</h5>
                      <p>{factor.description}</p>
                    </div>
                    <div className={`factor-trend ${factor.trend}`}>
                      {factor.trend === 'up' ? <FaArrowUp /> : <FaArrowDown />}
                    </div>
                  </div>
                  <div className="breakdown-bar">
                    <motion.div
                      className="bar-fill"
                      initial={{ width: 0 }}
                      whileInView={{ width: `${factor.value}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, delay: index * 0.1 }}
                      style={{ backgroundColor: getScoreColor(factor.value * 6) }}
                    />
                    <div className="bar-labels">
                      <span>{factor.value}%</span>
                      <span>Weight: {factor.weight}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* ── Network Visualization ── */}
          <motion.div
            className="network-visualization"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            <h4>Your Credit Network</h4>

            <div className="network-graph-container">
              <svg className="network-graph-svg" viewBox="0 0 600 500">
                <defs>
                  <filter id="node-glow" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="4" result="blur"/>
                    <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
                  </filter>
                </defs>

                {/* Connection lines */}
                {connections.map((conn, idx) => (
                  <motion.line
                    key={idx}
                    x1={nodes[conn.from].x} y1={nodes[conn.from].y}
                    x2={nodes[conn.to].x}   y2={nodes[conn.to].y}
                    stroke="rgba(0,212,170,0.2)"
                    strokeWidth="1.2"
                    strokeDasharray="4 3"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: idx * 0.08 }}
                  />
                ))}

                {/* Nodes */}
                {Object.entries(nodes).map(([key, node], idx) => (
                  <g key={key}>
                    <motion.circle
                      cx={node.x} cy={node.y}
                      r={node.type === 'pod' ? 28 : 25}
                      fill={node.color}
                      opacity={0.9}
                      filter="url(#node-glow)"
                      className="network-node"
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 0.9 }}
                      transition={{ duration: 0.5, delay: idx * 0.08 }}
                      whileHover={{ scale: 1.2, opacity: 1 }}
                      onMouseEnter={() => hoveredNodeState[1](key)}
                      onMouseLeave={() => hoveredNodeState[1](null)}
                    />
                    <text x={node.x} y={node.type === 'pod' ? node.y - 5 : node.y - 6}
                      textAnchor="middle" dominantBaseline="middle"
                      className="node-name-text">{node.name}</text>
                    {node.score && (
                      <text x={node.x} y={node.y + 9}
                        textAnchor="middle" dominantBaseline="middle"
                        className="node-score-text">{node.score}</text>
                    )}
                    {node.type === 'pod' && (
                      <text x={node.x} y={node.y + 16}
                        textAnchor="middle" dominantBaseline="middle"
                        className="pod-icon-text">👥</text>
                    )}
                  </g>
                ))}
              </svg>
            </div>

            {/* Benefits */}
            <div className="network-benefits">
              <h4>Benefits of Good Score</h4>
              <div className="benefits-grid">
                {[
                  { icon: '💸', title: 'Higher Loan Limits',      desc: 'Access larger amounts from friends' },
                  { icon: '📉', title: 'Lower Interest Rates',    desc: 'Better terms from P2P lenders' },
                  { icon: '👥', title: 'Extended Network Access', desc: "Borrow from friend's friends" },
                  { icon: '⚡', title: 'Faster Approvals',        desc: 'Instant loan approvals' }
                ].map((b, i) => (
                  <div key={i} className="benefit">
                    <div className="benefit-icon">{b.icon}</div>
                    <h5>{b.title}</h5>
                    <p>{b.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* ── Extended Network Banner ── */}
        <motion.div
          className="extended-network"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div className="extended-network-content">
            <h3>✨ Borrow from Friend's Friend</h3>
            <p>
              With a good Rupayya Score, access loans from extended networks.<br />
              Your friend's connections can see your creditworthiness and lend to you.
            </p>
            <div className="network-steps">
              {[
                { number: '1', title: 'Build Your Score',    desc: 'Maintain good repayment history with direct friends' },
                { number: '2', title: 'Extend Network',      desc: 'Your score becomes visible to 2nd degree connections' },
                { number: '3', title: 'Access More Capital', desc: 'Borrow from trusted extended network members' }
              ].map((step, idx) => (
                <div key={idx} className="step">
                  <div className="step-number">{step.number}</div>
                  <div className="step-content">
                    <h5>{step.title}</h5>
                    <p>{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default SocialCreditGraph;
